import confetti from "canvas-confetti";
import { useEffect, useState } from "react";
import {
  TQuizQuestion,
  TTeamMatchAnswer,
  TTeamMatchQuestion,
} from "services/types";
import { useEventStore, useGuestStore } from "store";

export const confettiShoot = () => {
  confetti({
    particleCount: 200,
    angle: 60,
    spread: 55,
    origin: { x: -0.1, y: 1.1 },
    colors: [
      "#ff3838",
      "#ff9f1a",
      "#32ff7e",
      "#18dcff",
      "#7d5fff",
      "#ff3838",
      "#ffd32a",
      "#ff006e",
    ],
  });

  confetti({
    particleCount: 200,
    angle: 120,
    spread: 55,
    origin: { x: 1.1, y: 1.1 },
    colors: [
      "#ff3838",
      "#ff9f1a",
      "#32ff7e",
      "#18dcff",
      "#7d5fff",
      "#ff3838",
      "#ffd32a",
      "#ff006e",
    ],
  });

  setTimeout(() => {
    confetti({
      particleCount: 150,
      angle: 60,
      spread: 55,
      origin: { x: 0.0, y: 0.8 },
      colors: [
        "#ff3838",
        "#ff9f1a",
        "#32ff7e",
        "#18dcff",
        "#7d5fff",
        "#ff3838",
        "#ffd32a",
        "#ff006e",
      ],
    });
    confetti({
      particleCount: 150,
      angle: 120,
      spread: 55,
      origin: { x: 1.0, y: 0.8 },
      colors: [
        "#ff3838",
        "#ff9f1a",
        "#32ff7e",
        "#18dcff",
        "#7d5fff",
        "#ff3838",
        "#ffd32a",
        "#ff006e",
      ],
    });
  }, 100);

  setTimeout(() => {
    confetti({
      particleCount: 150,
      angle: 120,
      spread: 60,
      origin: { x: 1, y: 0.5 },
      colors: [
        "#ff3838",
        "#ff9f1a",
        "#32ff7e",
        "#18dcff",
        "#7d5fff",
        "#ff3838",
        "#ffd32a",
        "#ff006e",
      ],
    });
    confetti({
      particleCount: 150,
      angle: 60,
      spread: 60,
      origin: { x: 0.0, y: 0.5 },
      colors: [
        "#ff3838",
        "#ff9f1a",
        "#32ff7e",
        "#18dcff",
        "#7d5fff",
        "#ff3838",
        "#ffd32a",
        "#ff006e",
      ],
    });
  }, 200);
};

type TTimerProps = {
  timerRef: React.MutableRefObject<number>;
  gameType: "quiz" | "team_match";
};

export const Timer = ({ timerRef, gameType }: TTimerProps) => {
  const { event } = useEventStore();
  const { guest } = useGuestStore();
  const [time, setTime] = useState(0);

  useEffect(() => {
    if (!event?.id || !guest?.id) return;
    let savedProgress;

    switch (gameType) {
      case "quiz":
        savedProgress = getQuizProgress(event.id, guest.id);
        break;
      case "team_match":
        savedProgress = getTeamMatchProgress(event.id, guest.id);
        break;
      default:
        savedProgress = null;
    }

    if (savedProgress) {
      setTime(savedProgress.timeSpent);
    }
  }, [event?.id, guest?.id, gameType]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime((prev) => {
        const newTime = prev + 1;
        timerRef.current = newTime;
        return newTime;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerRef]);

  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <div className="flex items-center justify-center">
      <span className="text-xl font-mono">{formatTime(time)}</span>
    </div>
  );
};

/////////////////////////////////////////////////
//QUIZ//
/////////////////////////////////////////////////

type QuizProgress = {
  answers: { [key: string]: string };
  currentQuestionIndex: number;
  score: number;
  timeSpent: number;
  questions: TQuizQuestion[];
};

export const getQuizProgress = (
  eventId: string,
  guestId: string
): QuizProgress | null => {
  try {
    const stored = localStorage.getItem(`quiz_progress_${eventId}_${guestId}`);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const saveQuizProgress = (
  eventId: string,
  guestId: string,
  progress: QuizProgress
) => {
  localStorage.setItem(
    `quiz_progress_${eventId}_${guestId}`,
    JSON.stringify(progress)
  );
};

export const clearQuizProgress = (eventId: string, guestId: string) => {
  localStorage.removeItem(`quiz_progress_${eventId}_${guestId}`);
};

/////////////////////////////////////////////////
//TEAM MATCH//
/////////////////////////////////////////////////

type TeamMatchProgress = {
  answers: { [key: string]: TTeamMatchAnswer };
  currentQuestionIndex: number;
  teamAScore: number;
  teamBScore: number;
  timeSpent: number;
  questions: TTeamMatchQuestion[];
};

export const getTeamMatchProgress = (
  eventId: string,
  guestId: string
): TeamMatchProgress | null => {
  try {
    const stored = localStorage.getItem(
      `team_match_progress_${eventId}_${guestId}`
    );
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const saveTeamMatchProgress = (
  eventId: string,
  guestId: string,
  progress: TeamMatchProgress
) => {
  localStorage.setItem(
    `team_match_progress_${eventId}_${guestId}`,
    JSON.stringify(progress)
  );
};

export const clearTeamMatchProgress = (eventId: string, guestId: string) => {
  localStorage.removeItem(`team_match_progress_${eventId}_${guestId}`);
};
